import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserAttribute,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

// Cognito configuration
const poolData = {
  UserPoolId: process.env.REACT_APP_USER_POOL_ID!,
  ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID!,
};

const userPool = new CognitoUserPool(poolData);

export interface AuthResult {
  success: boolean;
  message?: string;
  errorCode?: string;
  user?: CognitoUser;
  session?: CognitoUserSession;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
}

export interface LoginData {
  email: string;
  password: string;
}

class CognitoAuthService {
  // Register a new user
  async register(data: RegisterData): Promise<AuthResult> {
    return new Promise((resolve) => {
      const attributeList = [
        new CognitoUserAttribute({
          Name: 'email',
          Value: data.email,
        }),
        new CognitoUserAttribute({
          Name: 'name',
          Value: data.name,
        }),
      ];

      userPool.signUp(
        data.email,
        data.password,
        attributeList,
        [],
        (err, result) => {
          if (err) {
            console.error('Registration error:', err);
            resolve({
              success: false,
              message: err.message || 'Registration failed',
              errorCode: err.name,
            });
            return;
          }

          if (result) {
            resolve({
              success: true,
              message:
                'Registration successful. Please check your email for verification code.',
              user: result.user,
            });
          } else {
            resolve({
              success: false,
              message: 'Registration failed',
            });
          }
        }
      );
    });
  }

  // Confirm user registration with verification code
  async confirmRegistration(email: string, code: string): Promise<AuthResult> {
    return new Promise((resolve) => {
      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          console.error('Confirmation error:', err);
          resolve({
            success: false,
            message: err.message || 'Confirmation failed',
          });
          return;
        }

        resolve({
          success: true,
          message: 'Email confirmed successfully',
        });
      });
    });
  }

  // Resend verification code
  async resendConfirmationCode(email: string): Promise<AuthResult> {
    return new Promise((resolve) => {
      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          console.error('Resend code error:', err);
          resolve({
            success: false,
            message: err.message || 'Failed to resend code',
          });
          return;
        }

        resolve({
          success: true,
          message: 'Verification code sent successfully',
        });
      });
    });
  }

  // Login user
  async login(data: LoginData): Promise<AuthResult> {
    return new Promise((resolve) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: data.email,
        Password: data.password,
      });

      const userData = {
        Username: data.email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session) => {
          console.log('Login successful');
          resolve({
            success: true,
            message: 'Login successful',
            user: cognitoUser,
            session: session,
          });
        },
        onFailure: (err) => {
          console.error('Login error:', err);
          resolve({
            success: false,
            message: err.message || 'Login failed',
          });
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // Handle new password required case
          resolve({
            success: false,
            message: 'New password required. Please contact support.',
          });
        },
      });
    });
  }

  // Get current user
  getCurrentUser(): CognitoUser | null {
    return userPool.getCurrentUser();
  }

  // Get current session
  async getCurrentSession(): Promise<CognitoUserSession | null> {
    return new Promise((resolve) => {
      const cognitoUser = this.getCurrentUser();
      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: any, session: CognitoUserSession | null) => {
        if (err) {
          console.error('Get session error:', err);
          resolve(null);
          return;
        }
        resolve(session);
      });
    });
  }

  // Logout user
  async logout(): Promise<void> {
    const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID!;
    const domain = process.env.REACT_APP_OAUTH_DOMAIN!;
    const logoutUri = `${window.location.origin}/`;

    const lastAuthUser = localStorage.getItem(
      `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`
    );

    const isOAuthUser =
      lastAuthUser?.startsWith('Facebook_') ||
      lastAuthUser?.startsWith('Google_');

    if (isOAuthUser) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = `https://${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
      return;
    }

    const cognitoUser = this.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
  }

  // Forgot password
  async forgotPassword(email: string): Promise<AuthResult> {
    return new Promise((resolve) => {
      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve({
            success: true,
            message: 'Password reset code sent to your email',
          });
        },
        onFailure: (err) => {
          console.error('Forgot password error:', err);
          resolve({
            success: false,
            message: err.message || 'Failed to send reset code',
          });
        },
      });
    });
  }

  // Confirm new password
  async confirmPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<AuthResult> {
    return new Promise((resolve) => {
      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve({
            success: true,
            message: 'Password reset successful',
          });
        },
        onFailure: (err) => {
          console.error('Confirm password error:', err);
          resolve({
            success: false,
            message: err.message || 'Failed to reset password',
          });
        },
      });
    });
  }

  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    const session = await this.getCurrentSession();
    return session ? session.isValid() : false;
  }

  // Get user attributes
  async getUserAttributes(): Promise<Record<string, string> | null> {
    return new Promise((resolve) => {
      const cognitoUser = this.getCurrentUser();
      if (!cognitoUser) {
        resolve(null);
        return;
      }

      // First, ensure we have a valid session
      cognitoUser.getSession((sessionErr: any, session: CognitoUserSession | null) => {
        if (sessionErr || !session || !session.isValid()) {
          console.error('Get user attributes error: User session is not valid', sessionErr);
          resolve(null);
          return;
        }

        // Now get the user attributes with a valid session
        cognitoUser.getUserAttributes((err, attributes) => {
          if (err) {
            console.error('Get user attributes error:', err);
            resolve(null);
            return;
          }

          if (attributes) {
            const userAttributes: Record<string, string> = {};
            attributes.forEach((attribute) => {
              userAttributes[attribute.getName()] = attribute.getValue();
            });
            resolve(userAttributes);
          } else {
            resolve(null);
          }
        });
      });
    });
  }

  /**
   * Initiate Google OAuth login via Cognito Hosted UI
   */
  loginWithGoogle(): void {
    const domain = process.env.REACT_APP_OAUTH_DOMAIN;
    const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
    const redirectUri = `${window.location.origin}/auth/callback`;

    const oauthUrl = `https://${domain}/oauth2/authorize?` +
      `identity_provider=Google&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `scope=email openid profile&` +
      `prompt=select_account`;

    window.location.href = oauthUrl;
  }

  /**
   * Initiate Facebook OAuth login via Cognito Hosted UI
   */
  loginWithFacebook(): void {
    const domain = process.env.REACT_APP_OAUTH_DOMAIN;
    const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
    const redirectUri = `${window.location.origin}/auth/callback`;

    const oauthUrl = `https://${domain}/oauth2/authorize?` +
      `identity_provider=Facebook&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `scope=email openid profile&` +
      `prompt=select_account`;

    window.location.href = oauthUrl;
  }
}

export const cognitoAuthService = new CognitoAuthService();
